import styled from '@emotion/styled'
import React from 'react'
import { Search } from '@emotion-icons/evaicons-solid/Search'
import { ArrowUp } from '@emotion-icons/fluentui-system-filled/ArrowUp'
import { ArrowDown } from '@emotion-icons/fluentui-system-filled/ArrowDown'

import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { Colors } from 'src/components/theme'
import { MapTooltip } from 'src/v2/components/map-tooltip'
import { formatVal } from 'src/utils/number-format'

const IS_FINAL_VERSION = false // TODO: remove when implemented

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`

const Title = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: ${Colors.neutral950};
`

const DateRange = styled.div`
  font-weight: 500;
  font-size: 12px;
  color: ${Colors.neutral500};
`

const OpenButtonContainer = styled.div`
  margin: 0 -8px;
`

type StateTooltipHeadingProps = {
  state: string
  code: string
  dateRange: [string, string]
  onViewProperties: (state: string) => void
}

const StateTooltipHeading: React.FC<StateTooltipHeadingProps> = ({
  state,
  code,
  dateRange: [startDate, endDate],
  onViewProperties,
}) => {
  return (
    <HeadingContainer>
      <Title>{state}</Title>
      {IS_FINAL_VERSION && (
        <DateRange>
          {startDate} – {endDate}
        </DateRange>
      )}
      <OpenButtonContainer>
        <ButtonWithIcon ghost onClick={() => onViewProperties(code)}>
          <Search size={16} /> View Properties in this state
        </ButtonWithIcon>
      </OpenButtonContainer>
    </HeadingContainer>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const OperationSummaryContainer = styled.div``

const OperationSummaryLabel = styled.div`
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 14px;
  color: ${Colors.neutral400};
`

const OperationSummaryValue = styled.div`
  display: flex;
  gap: 12px;
  font-weight: 700;
  font-size: 16px;
  color: ${Colors.neutral700};
`

type StateOperationSummary = {
  total: number
  count: number
}

type StateTooltipProps = {
  state: string
  code: string
  startDate: string
  endDate: string
  purchased?: StateOperationSummary
  sold?: StateOperationSummary
  totalParcels?: number
  onViewProperties: (state: string) => void
  onMouseEnter: () => void
  onMouseLeave: () => void
}

export const StateTooltip: React.FC<StateTooltipProps> = ({
  state,
  code,
  startDate,
  endDate,
  purchased,
  sold,
  onViewProperties,
  onMouseEnter,
  onMouseLeave,
  totalParcels,
}) => {
  return (
    <MapTooltip
      heading={
        <StateTooltipHeading
          state={state}
          code={code}
          dateRange={[startDate, endDate]}
          onViewProperties={onViewProperties}
        />
      }
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      <Container>
        {!IS_FINAL_VERSION && (
          <OperationSummaryContainer>
            <OperationSummaryLabel>Total Properties:</OperationSummaryLabel>
            <OperationSummaryValue>{totalParcels ?? 0}</OperationSummaryValue>
          </OperationSummaryContainer>
        )}
        {IS_FINAL_VERSION && (
          <>
            {purchased && (
              <OperationSummaryContainer>
                <OperationSummaryLabel>Purchased</OperationSummaryLabel>
                <OperationSummaryValue>
                  <ArrowUp size={16} color={Colors.primaryGreen600} /> {formatVal(purchased.total, 'currency')} (
                  {purchased.count} Properties)
                </OperationSummaryValue>
              </OperationSummaryContainer>
            )}
            {sold && (
              <OperationSummaryContainer>
                <OperationSummaryLabel>Sold</OperationSummaryLabel>
                <OperationSummaryValue>
                  <ArrowDown size={16} color={Colors.primaryRed500} /> {formatVal(sold.total, 'currency')} ({sold.count}{' '}
                  Properties)
                </OperationSummaryValue>
              </OperationSummaryContainer>
            )}
          </>
        )}
      </Container>
    </MapTooltip>
  )
}
